import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  useTheme,
  Typography,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import Header from "../../components/VersatileComponents/Header";
import useCollectionData from "../../hooks/useCollectionData";
import {
  collection,
  query,
  where,
  getCountFromServer,
  getFirestore,
} from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import getGrading from "../../api/services/grading/get_grading";
import editGrading from "../../api/services/grading/set_grading";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import useUserClaims from "../../hooks/useUserClaims";

const CallCenterGrading = () => {
  const theme = useTheme();
  const db = getFirestore();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = React.useContext(SpinnerContext);
  const userClaims = useUserClaims(user);
  const { data: callcenters } = useCollectionData("callcenter");
  const [timeline, setTimeline] = useState("15days");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("total");
  const [gradingScales, setGradingScales] = useState(null);
  const [editMode, setEditMode] = useState({
    grade1: false,
    grade2: false,
    grade3: false,
    grade4: false,
  });
  const [editValues, setEditValues] = useState({
    grade1: { grade: "", icon: "" },
    grade2: { grade: "", icon: "" },
    grade3: { grade: "", icon: "" },
    grade4: { grade: "", icon: "" },
  });

  // Fetch grading scales
  const fetchGradingScales = useCallback(async () => {
    try {
      const gradingData = await getGrading("callcenter");
      setGradingScales(gradingData);
      setEditValues({
        grade1: {
          grade: gradingData.grade1.grade.toString(),
          icon: gradingData.grade1.icon,
        },
        grade2: {
          grade: gradingData.grade2.grade.toString(),
          icon: gradingData.grade2.icon,
        },
        grade3: {
          grade: gradingData.grade3.grade.toString(),
          icon: gradingData.grade3.icon,
        },
        grade4: {
          grade: gradingData.grade4.grade.toString(),
          icon: gradingData.grade4.icon,
        },
      });
    } catch (error) {
      console.error("Failed to fetch grading scales:", error);
      openSnackbar("Failed to fetch grading scales", "error");
    }
  }, [openSnackbar]);

  React.useEffect(() => {
    fetchGradingScales();
  }, [fetchGradingScales]);

  // Handle input change for editing thresholds and icons
  const handleInputChange = (gradeKey, field, value) => {
    setEditValues((prev) => ({
      ...prev,
      [gradeKey]: {
        ...prev[gradeKey],
        [field]: value,
      },
    }));
  };

  // Handle edit button click
  const handleEditButtonClick = (gradeKey) => {
    setEditMode((prev) => ({
      ...prev,
      [gradeKey]: true,
    }));
  };

  // Handle save button click
  const handleSaveButtonClick = async (gradeKey) => {
    const { grade, icon } = editValues[gradeKey];
    if (grade === "" || isNaN(parseInt(grade))) {
      openSnackbar(
        `Grade ${gradeKey.toUpperCase()} threshold cannot be empty or invalid!`,
        "error"
      );
      setIsSubmitting(false);
      return;
    }
    if (icon === "") {
      openSnackbar(
        `Grade ${gradeKey.toUpperCase()} icon cannot be empty!`,
        "error"
      );
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setEditMode((prev) => ({
      ...prev,
      [gradeKey]: false,
    }));

    try {
      await editGrading(
        user,
        {
          [`${gradeKey}.grade`]: parseInt(grade, 10),
          [`${gradeKey}.icon`]: icon,
        },
        "callcenter"
      );
      openSnackbar(
        `Grade ${gradeKey.toUpperCase()} updated successfully!`,
        "success"
      );
      await fetchGradingScales(); // Refresh grading scales
    } catch (error) {
      openSnackbar(error.message || "An unexpected error occurred.", "error");
      setEditMode((prev) => ({
        ...prev,
        [gradeKey]: true, // Revert to edit mode on error
      }));
    }
    setIsSubmitting(false);
  };

  // Calculate date range for timeline
  const getDateRange = () => {
    const now = new Date();
    let startDate;
    startDate = new Date(now);
    switch (timeline) {
      case "15days":
        startDate.setDate(startDate.getDate() - 15);
        break;
      case "1month":
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case "6month":
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case "1year":
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      default:
        startDate.setMonth(startDate.getMonth() - 1);
    }
    return startDate;
  };

  // Fetch order counts for a call center
  const fetchOrderCounts = async (callCenterId, startDate) => {
    const orderTypes = ["Asbeza", "Card", "Water", "Wifi"];
    const counts = {};
    for (const type of orderTypes) {
      const q = query(
        collection(db, type),
        where("callcenterId", "==", callCenterId),
        where("createdAt", ">=", startDate)
      );
      const snapshot = await getCountFromServer(q);
      counts[type] = snapshot.data().count;
    }
    counts.total = Object.values(counts).reduce((sum, count) => sum + count, 0);
    return counts;
  };

  // Fetch counts for all call centers
  const [gradingData, setGradingData] = useState([]);
  React.useEffect(() => {
    const fetchAllCounts = async () => {
      const startDate = getDateRange();
      const data = await Promise.all(
        callcenters.map(async (center) => {
          const counts = await fetchOrderCounts(center.id, startDate);
          return {
            id: center.id,
            name: center.fullName || center.id,
            ...counts,
          };
        })
      );
      setGradingData(data);
    };
    if (callcenters.length > 0) {
      fetchAllCounts();
    }
  }, [callcenters, timeline]);

  // Sorting logic
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    const sorted = [...gradingData].sort((a, b) => {
      const valueA = a[orderBy] || (orderBy === "name" ? "" : 0);
      const valueB = b[orderBy] || (orderBy === "name" ? "" : 0);
      if (orderBy === "name") {
        return order === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      return order === "asc" ? valueA - valueB : valueB - valueA;
    });
    return sorted.map((row, index) => ({ ...row, rank: index + 1 }));
  }, [gradingData, order, orderBy]);

  // Apply grading based on difference from average
  const gradedData = useMemo(() => {
    if (sortedData.length === 0 || !gradingScales) return sortedData;

    const totals = sortedData.map((c) => c.total);
    const average =
      totals.reduce((acc, total) => acc + total, 0) / sortedData.length;

    return sortedData.map((center) => {
      const diffFromAverage = center.total - average;
      let grade, icon;

      switch (true) {
        case diffFromAverage >= gradingScales.grade1.grade:
          grade = gradingScales.grade1.value;
          icon = gradingScales.grade1.icon;
          break;
        case diffFromAverage >= gradingScales.grade2.grade:
          grade = gradingScales.grade2.value;
          icon = gradingScales.grade2.icon;
          break;
        case diffFromAverage >= gradingScales.grade3.grade:
          grade = gradingScales.grade3.value;
          icon = gradingScales.grade3.icon;
          break;
        case diffFromAverage >= gradingScales.grade4.grade:
          grade = gradingScales.grade4.value;
          icon = gradingScales.grade4.icon;
          break;
        default:
          grade = "F";
          icon = "❌";
      }

      return {
        ...center,
        grade,
        icon,
      };
    });
  }, [sortedData, gradingScales]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Call Center Grading</title>
        <meta
          name="description"
          content="Ranking of call centers by order counts"
        />
      </Helmet>
      <Box
        m="1.5rem 2.5rem"
        sx={{
          backgroundColor: theme.palette.background.default,
          height: "100%",
          position: "relative",
        }}
      >
        <Header
          title="Call Center Grading"
          subtitle="Ranking by Order Counts"
        />
        {gradingScales && (
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography variant="h6" color={theme.palette.secondary[50]}>
              Grading Scales (Difference from Average Orders)
            </Typography>
            <List>
              {[
                { key: "grade1", data: gradingScales.grade1 },
                { key: "grade2", data: gradingScales.grade2 },
                { key: "grade3", data: gradingScales.grade3 },
                { key: "grade4", data: gradingScales.grade4 },
              ].map(({ key, data }) => (
                <ListItem key={key}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <ListItemText
                        primary={
                          editMode[key] ? (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <TextField
                                value={editValues[key].icon}
                                onChange={(e) =>
                                  handleInputChange(key, "icon", e.target.value)
                                }
                                size="small"
                                sx={{ width: "60px" }}
                                placeholder="Icon"
                                disabled={
                                  !(userClaims.superAdmin || userClaims.admin)
                                }
                              />
                              <TextField
                                value={editValues[key].grade}
                                type="number"
                                onChange={(e) =>
                                  handleInputChange(
                                    key,
                                    "grade",
                                    e.target.value
                                  )
                                }
                                size="small"
                                sx={{ width: "100px" }}
                                placeholder="Threshold"
                                disabled={
                                  !(userClaims.superAdmin || userClaims.admin)
                                }
                              />
                            </Box>
                          ) : (
                            `${data.icon} Grade ${data.value}: ≥ ${data.grade}`
                          )
                        }
                        sx={{ color: theme.palette.secondary[50] }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      {(userClaims.superAdmin || userClaims.admin) &&
                        (editMode[key] ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSaveButtonClick(key)}
                          >
                            Save
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleEditButtonClick(key)}
                          >
                            Edit
                          </Button>
                        ))}
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
              <ListItem>
                <ListItemText
                  primary="❌ Grade F: < -200"
                  sx={{ color: theme.palette.secondary[50] }}
                />
              </ListItem>
            </List>
          </Box>
        )}
        <FormControl sx={{ mt: 4, mb: 2, minWidth: 120 }}>
          <InputLabel>Timeline</InputLabel>
          <Select
            value={timeline}
            label="Timeline"
            onChange={(e) => setTimeline(e.target.value)}
            sx={{
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
            }}
          >
            <MenuItem value="15days">15 Days</MenuItem>
            <MenuItem value="1month">1 Month</MenuItem>
            <MenuItem value="6month">6 Months</MenuItem>
            <MenuItem value="1year">1 Year</MenuItem>
          </Select>
        </FormControl>
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[200],
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#323E8B" : "#C5C7D7"
            }`,
            borderRadius: ".1rem",
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.background.alt,
                }}
              >
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  Rank
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleRequestSort("name")}
                  >
                    Call Center
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Asbeza"}
                    direction={orderBy === "Asbeza" ? order : "desc"}
                    onClick={() => handleRequestSort("Asbeza")}
                  >
                    Asbeza
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Card"}
                    direction={orderBy === "Card" ? order : "desc"}
                    onClick={() => handleRequestSort("Card")}
                  >
                    Card
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Water"}
                    direction={orderBy === "Water" ? order : "desc"}
                    onClick={() => handleRequestSort("Water")}
                  >
                    Water
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Wifi"}
                    direction={orderBy === "Wifi" ? order : "desc"}
                    onClick={() => handleRequestSort("Wifi")}
                  >
                    Wifi
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "total"}
                    direction={orderBy === "total" ? order : "desc"}
                    onClick={() => handleRequestSort("total")}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  Grade
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gradedData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333964" : "white",
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.rank}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.name}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Asbeza || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Card || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Water || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Wifi || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.total || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.grade}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.icon}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default CallCenterGrading;
